import { Position, type InternalNode } from '@xyflow/react';

// this helper function returns the intersection point of the line between the center of the intersectionNode and the targetNode
function getNodeIntersection(intersectionNode: InternalNode, targetNode: InternalNode) {
  // https://math.stackexchange.com/questions/1728913/finding-the-intersection-between-a-line-and-a-rectangle
  const { width: intersectionNodeWidth, height: intersectionNodeHeight } = intersectionNode.measured || { width: 0, height: 0 };
  const intersectionNodePos = intersectionNode.internals?.positionAbsolute || { x: 0, y: 0 };
  const targetNodePos = targetNode.internals?.positionAbsolute || { x: 0, y: 0 };

  const w = (intersectionNodeWidth ?? 0) / 2;
  const h = (intersectionNodeHeight ?? 0) / 2;

  const x2 = intersectionNodePos.x + w;
  const y2 = intersectionNodePos.y + h;
  const x1 = targetNodePos.x + (targetNode.measured?.width ?? 0) / 2;
  const y1 = targetNodePos.y + (targetNode.measured?.height ?? 0) / 2;

  const dx = x2 - x1;
  const dy = y2 - y1;

  if (dx === 0 && dy === 0) {
    return { x: x2, y: y2 };
  }

  const t = Math.min(Math.abs(w / dx), Math.abs(h / dy));

  return {
    x: x2 - t * dx,
    y: y2 - t * dy,
  };
}

// returns the position (top, right, bottom or left) of the node based on the intersection point
function getEdgePosition(node: InternalNode, intersectionPoint: { x: number; y: number }) {
  const n = node.internals?.positionAbsolute || { x: 0, y: 0 };
  const w = (node.measured?.width ?? 0) / 2;
  const h = (node.measured?.height ?? 0) / 2;

  const x = intersectionPoint.x - (n.x + w);
  const y = intersectionPoint.y - (n.y + h);

  if (Math.abs(x / w) > Math.abs(y / h)) {
    return x > 0 ? Position.Right : Position.Left;
  }
  return y > 0 ? Position.Bottom : Position.Top;
}

// returns the parameters (x, y, position) needed to create an edge
export function getEdgeParams(source: InternalNode, target: InternalNode) {
  const sourceIntersectionPoint = getNodeIntersection(source, target);
  const targetIntersectionPoint = getNodeIntersection(target, source);

  const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
  const targetPos = getEdgePosition(target, targetIntersectionPoint);

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos,
  };
}
